import { FormControl, FormLabel, RadioGroup } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { CartModifierGroup } from '../../../utils/cart';
import {
  checkItemInCartItemTree,
  isDefaultItemInModGroup,
  isItem86edToday,
} from '../../../utils/menu';
import { ModGroupLabel } from '../components/ModGroupLabel';
import RecursiveModGroup from '../menu/RecursiveModGroup';
import SingularModChoice from '../components/SingularModChoice';
import {
  getCartItemDescription,
  getModGroupItemLabel,
  sortAndFilterMenuItems,
} from '../util/helper';
import { IModGroup } from './modGroup.props';
import { canShowItem, hasModGroupChildren } from './modGroup.utils';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import Colors from '../../../utils/color';
import { isBMModifierAnUnavailableConstraint } from '../../../utils/menuUnification.utils';
import { useSelector } from 'react-redux';
import { selectUnavailableItemsByName } from '../../../selectors/menu';

const useStyles = makeStyles<{
  hasChildren: boolean;
  isHighlighted: boolean;
  isLabelClickable: boolean;
}>()(({ spacing }, { hasChildren, isHighlighted, isLabelClickable }) => {
  const bgColor = isHighlighted ? Colors.alto : Colors.white;
  const mBottom = hasChildren ? spacing(1.25) : 0;
  return {
    modGroup: {
      margin: '0 !important',
      '& legend': {
        width: '100%',
      },
      width: '100%',
      backgroundColor: bgColor,
    },
    modGroupContainer: {
      marginBottom: mBottom,
    },
    modGroupLabel: {
      cursor: isLabelClickable ? 'pointer' : 'default',
    },
  };
});

export default function SingularSelectionModGroup(props: IModGroup) {
  const {
    modGroup,
    onChange,
    cartItem,
    pathToItem,
    parentModifier,
    isHighlighted,
    isSelectedToFocus,
    sendModGroupTTSMessage,
    sendTTSMessage,
  } = props;

  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  let currentValue: string | null = null;
  let selectedModNumber = 0;
  const currentItem = checkItemInCartItemTree({
    cartItem,
    pathToItem,
  }) as CartModifierGroup;
  if (currentItem) {
    const selectedMod = currentItem?.selectedItems || [];
    currentValue = Object.values(selectedMod)[0]?.itemId || null;
    selectedModNumber = Object.values(selectedMod).length;
  }

  const isBrandMenu = getMenuUnificationFeatureFlag();

  // sort and filter the modifiers before rendering
  const menuItems = isBrandMenu
    ? Object.values(modGroup.menuItems)
    : sortAndFilterMenuItems(modGroup.menuItems);

  const key = [cartItem.id, modGroup.id].join('_');
  const hasChildren = hasModGroupChildren(props);
  const isLabelClickable = !!(isBrandMenu && modGroup.description);

  const { classes } = useStyles({
    hasChildren,
    isHighlighted: !!isHighlighted || !!isSelectedToFocus,
    isLabelClickable,
  });

  const handleLabelClick = () => {
    if (isLabelClickable) {
      sendModGroupTTSMessage(modGroup);
    }
  };

  return (
    <div key={key} className={classes.modGroupContainer}>
      <FormControl
        component="fieldset"
        className={classes.modGroup}
        data-testid="mod-group-form"
      >
        <div onClick={handleLabelClick}>
          <FormLabel className={classes.modGroupLabel}>
            <ModGroupLabel
              modGroup={modGroup}
              selectedModNumber={selectedModNumber}
              parentModifier={parentModifier}
            />
          </FormLabel>
        </div>
        <RadioGroup
          row
          aria-label={modGroup.name}
          name={modGroup.name}
          value={currentValue}
          onChange={onChange(modGroup)}
        >
          {menuItems.map((child) => {
            const is86edToday = isItem86edToday(child);
            const pathToModChoice = [pathToItem, child.id].join('__');
            const isSelected = !!checkItemInCartItemTree({
              cartItem,
              pathToItem: pathToModChoice,
            });
            const id = child.itemId;
            const name = child.name;
            const modLabel = getModGroupItemLabel(child);

            const isDefaultItem = isDefaultItemInModGroup(id, modGroup);

            const isItemUnavailableNow = isBrandMenu
              ? isBMModifierAnUnavailableConstraint(
                  cartItem,
                  unavailableItemsByName,
                  parentModifier,
                  pathToItem,
                  modGroup.name,
                  child.name
                )
              : false;

            const showItem = canShowItem({
              isBrandMenu,
              unavailableItemsByName,
              itemName: name,
              cartItem,
              modGroupName: modGroup.name,
              childAvailability: child?.available,
              is86edToday,
            });

            if (showItem) {
              const childDescription = getCartItemDescription(
                child,
                isBrandMenu
              );
              const onInfoClick = childDescription
                ? () => sendTTSMessage(childDescription)
                : undefined;
              return (
                <SingularModChoice
                  key={pathToModChoice}
                  id={id}
                  name={name}
                  modLabel={modLabel}
                  is86edToday={
                    isBrandMenu
                      ? !child.available || is86edToday || isItemUnavailableNow
                      : is86edToday
                  }
                  isSelected={isSelected}
                  isDefault={isDefaultItem}
                  onInfoClick={onInfoClick}
                  description={childDescription}
                />
              );
            }
            return null;
          })}
        </RadioGroup>
      </FormControl>
      <RecursiveModGroup {...props} />
    </div>
  );
}
